import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { env } from 'src/config/env'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { CodeFormField } from 'src/shared/components/ui/forms/form-fields/code'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { useConsumerSignInStart, useConsumerSignInComplete } from 'src/shared/features/auth/api'
import { useToast } from 'src/shared/hooks/use-toast'
import {
  consumerLoginEmailSchema,
  type ConsumerLoginEmailFormData,
  consumerLoginOtpSchema,
  type ConsumerLoginOtpFormData,
} from 'src/shared/schemas/forms/consumer-auth'

interface ConsumerLoginFormProps {
  onSuccess: () => void
}

const ConsumerLoginForm = ({ onSuccess }: ConsumerLoginFormProps) => {
  const [step, setStep] = useState<'email' | 'otp'>('email')
  const [signInData, setSignInData] = useState<any>(null)
  const { toast } = useToast()

  // Email form
  const emailForm = useForm<ConsumerLoginEmailFormData>({
    resolver: zodResolver(consumerLoginEmailSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  })

  // OTP form
  const otpForm = useForm<ConsumerLoginOtpFormData>({
    resolver: zodResolver(consumerLoginOtpSchema),
    mode: 'onSubmit',
    defaultValues: {
      otp: '',
    },
  })

  // Hooks for sign-in process
  const signInStart = useConsumerSignInStart()
  const signInComplete = useConsumerSignInComplete()

  // Handle email submission
  const handleEmailSubmit = async (data: ConsumerLoginEmailFormData) => {
    try {
      const result = await signInStart.mutateAsync(data.email)
      setSignInData(result)
      setStep('otp')

      toast({
        title: 'Verification code sent',
        description: 'Please check your email for the verification code',
      })
    } catch (error) {
      console.error('Sign-in start error:', error)

      if (error instanceof Error) {
        // For user not found, give a clearer message
        if (error.name === 'UserNotFoundException') {
          toast({
            title: 'Account not found',
            description: 'No account exists with this email. Please sign up first.',
            variant: 'destructive',
          })
          return
        }

        toast({
          title: `Sign in failed: ${error.name}`,
          description: error.message || 'Failed to start sign in process',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Sign in failed',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        })
      }
    }
  }

  // Handle OTP submission
  const handleOtpSubmit = async (data: ConsumerLoginOtpFormData) => {
    try {
      await signInComplete.mutateAsync({
        challengeResponse: data.otp,
        signInResult: signInData,
      })

      toast({
        title: 'Welcome back!',
        description: 'You have successfully signed in',
      })

      onSuccess()
    } catch (error) {
      console.error('OTP verification error:', error)

      if (error instanceof Error) {
        toast({
          title: `Verification failed: ${error.name}`,
          description: error.message || 'Failed to verify code',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Verification failed',
          description: 'An unexpected error occurred. Please try again with a new code.',
          variant: 'destructive',
        })
      }
    }
  }

  // Go back to email input
  const handleBack = () => {
    setStep('email')
    setSignInData(null)
  }

  return (
    <div className="mt-6 space-y-6">
      {step === 'email' ? (
        <>
          <h2 className="text-center text-3xl font-bold text-primary">Welcome back</h2>
          <Form {...emailForm}>
            <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="space-y-4">
              <InputFormField
                control={emailForm.control}
                name="email"
                label="Email address"
                type="email"
                placeholder="Email address"
                required
              />
              <div className="mt-4">
                <p className="text-xs text-muted">
                  We&apos;ll email you a code to confirm your email.{' '}
                  <a href={env.LINK_TO_PRIVACY_POLICY} className="font-medium text-primary underline">
                    Privacy Policy
                  </a>
                </p>
              </div>
              <Button type="submit" className="w-full py-4" isLoading={signInStart.isPending}>
                {signInStart.isPending ? 'Sending Code...' : 'Continue'}
              </Button>
            </form>
          </Form>
        </>
      ) : (
        <Form {...otpForm}>
          <form onSubmit={otpForm.handleSubmit(handleOtpSubmit)} className="space-y-4">
            <h3 className="text-center text-xl font-medium text-primary">Confirm your email</h3>
            <p className="text-center text-sm text-muted">
              You&apos;ve received a code at your email address. Please check your inbox and spam folders to retrieve
              it.
            </p>

            <CodeFormField control={otpForm.control} name="otp" className="py-2" length={8} />

            <div className="flex flex-col gap-2">
              <Button type="submit" className="w-full py-4 " isLoading={signInComplete.isPending}>
                {signInComplete.isPending ? 'Verifying...' : 'Verify & sign in'}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="mt-2 text-sm text-muted hover:underline"
                onClick={handleBack}
              >
                Try a different email address
              </Button>
            </div>
          </form>
        </Form>
      )}
    </div>
  )
}

export default ConsumerLoginForm
